import React, { useEffect } from 'react';
import { Box, CircularProgress } from '@material-ui/core';
import { FormikProps } from 'formik';
import useAddControlsStyles
    from 'modules/clients/content/TimeAndExpensePage/SheetsInProgress/AddEntryControls/AddControlsStyles';
import { useSelector } from 'react-redux';
import { EntryType, QuantityType } from 'shared/models/sheet/Sheet';
import UpperButton from 'shared/components/buttons/UpperButton';
import ExpenseDataField from 'shared/components/formSpecialFields/expenseData/ExpenseDataField';
import ZipCodeField from 'shared/components/formSpecialFields/zipCode/ZipCodeField';
import FormField from 'shared/components/formFields/FormField';
import { selectIsInitialLoadingTimeAndExpense } from 'store/components/workingSheets/workingSheetsSelectors';
import { IPayPeriod } from 'store/entities/timesheet/models/PayPeriod';
import { CommonEntryInlineFields, IBaseCommonEntryFieldsProps } from './CommonEntryInlineFields';
import { defaultValues, IExpenseEntryFormValues } from './ExpenseEntryModel';
import { EntrySlug, InputFields } from 'store/entities/clients/clientsModel';
import { AttachMoney } from '@material-ui/icons';
import { ICommonEntryFormValues } from './EntryCommonFields';
import { showField } from '../utils';

export interface IExpenseEntryFormProps extends FormikProps<IExpenseEntryFormValues>, IBaseCommonEntryFieldsProps {
    payPeriod: IPayPeriod;
    areaId?: number | null;
    inputs: InputFields;
    onChangeCommonValues: (values: ICommonEntryFormValues) => void;
    disabled?: boolean;
}

export function ExpenseEntryInlineForm({
    supervisorId,
    areaId,
    payPeriod,
    setFieldValue,
    setTouched,
    handleSubmit,
    inputs,
    userId,
    onChangeCommonValues,
    disabled = false,
    ...props
}: IExpenseEntryFormProps) {
    const classes = useAddControlsStyles();
    const { projectAssignment, activity, data } = props.values;
    const assignmentId = projectAssignment?.assignment?.id;
    const isLoading = useSelector(selectIsInitialLoadingTimeAndExpense);

    useEffect(() => {
        setFieldValue('zipCode', defaultValues.zipCode);
    }, [assignmentId, setFieldValue]);

    return (
        <form onSubmit={handleSubmit} className={classes.form}>
            <Box className={classes.controlsContainer}>
                <Box className={classes.aSide}>
                    <CommonEntryInlineFields
                        values={props.values}
                        onChangeCommonValues={onChangeCommonValues}
                        setFieldValue={setFieldValue}
                        setTouched={setTouched}
                        payPeriod={payPeriod}
                        supervisorId={supervisorId}
                        // @ts-ignore
                        areaId={areaId}
                        entryType={EntryType.EXPENSE}
                        userId={userId}
                        inputs={inputs}
                        disabled={disabled}
                    />

                    <ExpenseDataField
                        name="data"
                        label="Expense"
                        key={`${activity?.data_type ?? props.values.activity?.data_type}.${data?.entry_type}`}
                        activity={activity ?? props.values.activity}
                        className={classes.selectFieldSmall}
                        inputProps={{
                            showOdometerDollarsInput: true,
                        }}
                        disabled={disabled}
                    />

                    {showField(inputs, EntrySlug.Notes) && activity?.data_type !== QuantityType.ODOMETER && (
                        <FormField
                            name="notes"
                            label={inputs.notes.placeholder}
                            className={classes.fixedLarge}
                            disabled={disabled}
                        />
                    )}

                    {showField(inputs, EntrySlug.ZipCode) && activity?.show_zip_code && (
                        <ZipCodeField
                            name="zipCode"
                            label={inputs.zip_code.placeholder}
                            className={classes.fixedMedium}
                            disabled={disabled}
                        />
                    )}
                </Box>

                <UpperButton
                    label={(
                        <>
                            <span>
                           + Add
                            </span>
                            {isLoading ? (
                                <Box className={classes.loaderIconContainer}>
                                    <CircularProgress className={classes.loaderIcon} />
                                </Box>
                            ) : (
                                <AttachMoney className={classes.addIcon} />
                            )}
                        </>
                    )}
                    className={classes.upperButton}
                    disabled={disabled || isLoading}
                />
            </Box>
        </form>
    );
}
